import './variables.css'
import './tailwind.css'
import './app.css'
import './dots.css'
import '@melloware/coloris/dist/coloris.css'
import './store'
import { Component, createMemo, Show, lazy, onMount, Switch, Match, createEffect } from 'solid-js'
import { Route, Router, useLocation, useNavigate } from '@solidjs/router'
import NavBar from './shared/NavBar'
import Notifications from './Toasts'
import CharacterRoutes from './pages/Character'
import ScenarioRoutes from './pages/Scenario'
import { settingStore } from './store/settings'
import { userStore } from './store/user'
import LoginPage from './pages/Login'
import HomePage from './pages/Home'
import WorkshopPage from './pages/Home/WorkshopPage'
import TrendsPage from './pages/Home/TrendsPage'
import TrendingPage from './pages/Home/TrendingPage'
import Navigation from './Navigation'
import BottomNav from './shared/BottomNav'
import Loading from './shared/Loading'
import Button from './shared/Button'
import ImpersonateModal from './pages/Character/ImpersonateModal'
import ChubRoutes from './pages/Chub'
import Redirect from './shared/Redirect'
import Maintenance from './shared/Maintenance'
import CharacterChats from './pages/Character/ChatList'
import ChatDetail from './pages/Chat/ChatDetail'
import Settings, { SettingsModal } from './pages/Settings'
import ProfilePage, { ProfileModal } from './pages/Profile'
import ProfileCenter from './pages/Profile/ProfileCenter'
import ProfileSettings from './pages/Profile/ProfileSettings'
import FeedbackPage from './pages/Profile/FeedbackPage'
import ChatHistory from './pages/Chat/ChatHistory'
import { useCharacterBg, usePaneManager, useWindowSize } from './shared/hooks'
import { For } from 'solid-js'
import { css, getMaxChatWidth } from './shared/util'
import FAQ from './pages/Home/FAQ'
import CreateChatForm from './pages/Chat/CreateChatForm'
import Modal from './shared/Modal'
import { ContextProvider } from './store/context'
import MemoryGuide from './pages/Guides/Memory'
import NovelGuide from './pages/Guides/NovelAI'
import { ImageModal } from './pages/Image/ImageModal'
import { CheckoutCancel, CheckoutSuccess } from './pages/Profile/Checkout'
import { markdown } from './shared/markdown'
import SoundsPage from './pages/Sounds'
import PatreonOauth from './pages/Settings/PatreonOauth'
import { SagaDetail } from './pages/Saga/Detail'
import { SagaList } from './pages/Saga/List'
import { ImageSettingsModal } from './pages/Settings/Image/ImageSettings'
import { ResetPasswordPage } from './pages/Login/ResetPassword'
import { api } from './store/api'
import { GlobalFileInput } from './shared/GlobalFileInput'
import { pageStore } from './store'
import { i18nStore } from './store/i18n'

const App: Component = () => {
  const i18n = i18nStore((s) => ({ language: s.language, t: s.t }))
  const state = userStore((s) => ({ user: s.user, loggedIn: s.loggedIn }))
  const cfg = settingStore((s) => ({ config: s.config }))
  const page = pageStore((s) => ({ flags: s.flags }))

  return (
    <Router root={Layout}>
      <CharacterRoutes />
      <ScenarioRoutes />
      <Route path="/discord" component={() => <Redirect external="https://" />} />
      <ChubRoutes />
      <Route path="/chats/create/:id?" component={() => <CreateChatForm />} />
      <Route path="/chats" component={CharacterChats} />
      <Route path="/history" component={ChatHistory} />
      <Route path="/chat" component={ChatDetail} />
      <Show when={cfg.config.guidanceAccess || state.user?.admin}>
        <Route path="/saga" component={SagaList} />
        <Route path="/saga/:id" component={SagaDetail} />
      </Show>
      <Route path="/chat/:id" component={ChatDetail} />
      <Route path="/workshop" component={WorkshopPage} />
      <Route path="/trends" component={TrendingPage} />
      <Route path="/community" component={TrendsPage} />
      <Route path={['/info', '/']} component={HomePage} />
      <Route path="/presets/:id" component={lazy(() => import('./pages/GenerationPresets'))} />
      <Route
        path="/presets"
        component={lazy(() => import('./pages/GenerationPresets/PresetList'))}
      />
      <Show when={page.flags.sounds}>
        <Route path="/sounds" component={SoundsPage} />
      </Show>
      <Route path="/oauth/patreon" component={PatreonOauth} />
      <Route path="/profile" component={ProfileCenter} />
      <Route path="/profile/settings" component={ProfileSettings} />
      <Route path="/profile/feedback" component={FeedbackPage} />
      <Route path="/profile/credit" component={lazy(() => import('./pages/Profile/CreditRecord'))} />
      <Route path="/settings" component={() => <Settings />} />
      <Route path="/memory" component={lazy(() => import('./pages/Memory/Library'))} />
      <Route path="/memory/:id" component={lazy(() => import('./pages/Memory/EditMemoryPage'))} />
      <Route path="/terms-of-service" component={lazy(() => import('./pages/TermsOfService'))} />
      <Route path="/checkout">
        <Route path="/success" component={CheckoutSuccess} />
        <Route path="/cancel" component={CheckoutCancel} />
      </Route>
      <Route path="/privacy-policy" component={lazy(() => import('./pages/PrivacyPolicy'))} />
      <Route path="/guides">
        <Route path="/memory" component={MemoryGuide} />
        <Route path="/novel" component={NovelGuide} />
      </Route>
      <Show when={state.loggedIn}>
        <Route path="/invites" component={lazy(() => import('./pages/Invite/InvitesPage'))} />
        <Show when={state.user?.admin}>
          <Route path="/admin/metrics" component={lazy(() => import('./pages/Admin/Metrics'))} />

          <Route
            path="/admin/configuration"
            component={lazy(() => import('./pages/Admin/Configuration'))}
          />

          <Route path="/admin/users" component={lazy(() => import('./pages/Admin/UsersPage'))} />
          <Route
            path="/admin/subscriptions"
            component={lazy(() => import('./pages/Admin/CreditManage'))}
          />
          <Route
            path="/admin/model-billing"
            component={lazy(() => import('./pages/Admin/ModelBilling'))}
          />
          <Route
            path="/admin/subscriptions/:id"
            component={lazy(() => import('./pages/Admin/SubscriptionModel'))}
          />
          <Route
            path={['/admin/announcements', '/admin/announcements/:id']}
            component={lazy(() => import('./pages/Admin/Announcements'))}
          />
          <Route path="/admin/tiers/:id" component={lazy(() => import('./pages/Admin/Tiers'))} />
        </Show>
      </Show>
      <Show when={cfg.config.canAuth}>
        <Route path={['/login', '/login/remember']} component={LoginPage} />
        <Route path="/recovery" component={ResetPasswordPage} />
      </Show>
      <Route path="/faq" component={FAQ} />
      <Route path="*" component={HomePage} />
    </Router>
  )
}

const Layout: Component<{ children?: any }> = (props) => {
  const i18n = i18nStore((s) => ({ language: s.language, t: s.t }))
  const page = pageStore((s) => ({
    showMenu: s.showMenu,
    confirm: s.confirm,
    showImpersonate: s.showImpersonate,
  }))
  const state = userStore((s) => ({ ui: s.ui, banned: s.banned, loggedIn: s.loggedIn, user: s.user }))
  const cfg = settingStore((s) => ({
    init: s.init,
    initLoading: s.initLoading,
    config: s.config,
  }))

  const location = useLocation()
  const navigate = useNavigate()
  const pane = usePaneManager()
  const size = useWindowSize()
  const isMobile = createMemo(() => size.platform() === 'sm')
  const isAdmin = createMemo(() => !!state.user?.admin)
  const showAdminNav = createMemo(() => isAdmin() && !isMobile())

  // Redirect unauthenticated users to login page (except for public pages)
  createEffect(() => {
    if (cfg.initLoading || !cfg.init) return
    
    const publicPaths = ['/login', '/login/remember', '/recovery', '/faq', '/terms-of-service', '/privacy-policy']
    const isPublicPath = publicPaths.some(path => location.pathname.startsWith(path))
    
    // If user is not logged in and trying to access a protected page, redirect to login
    if (!state.loggedIn && !isPublicPath && cfg.config.canAuth) {
      navigate('/login', { replace: true })
    }
  })

  const maxW = createMemo((): string => {
    if (pane.showing()) return 'max-w-full'

    return getMaxChatWidth(state.ui.chatWidth)
  })

  const reload = () => {
    settingStore.init()
  }

  onMount(() => {
    settingStore.init()
  })

  const isChat = createMemo(() => {
    return location.pathname.startsWith('/chat/') || location.pathname.startsWith('/saga/')
  })

  const bgStyles = useCharacterBg('layout')

  return (
    <ContextProvider>
      <style>{css}</style>
      <div class="scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-[var(--hl-900)] app flex flex-col justify-between" classList={{ 'pb-16': isMobile() }}>
        <NavBar />
        <div class="flex w-full grow flex-row overflow-y-hidden">
          <Show when={showAdminNav()}>
            <Navigation />
          </Show>

          <main
            id="main-content"
            class="w-full overflow-y-auto overflow-x-hidden"
            classList={{
              'sm:ml-[320px]': page.showMenu && showAdminNav(),
              'sm:ml-0': !page.showMenu || !showAdminNav(),
            }}
            data-background
            style={{ ...bgStyles(), 'scrollbar-gutter': 'stable' }}
          >
            <div
              class={`mx-auto h-full min-h-full ${isChat() ? maxW() : 'max-w-8xl'}`}
              classList={{
                'content-background': !isChat(),
                'max-w-[480px]': !isAdmin() && !isMobile(),
              }}
            >
              <Switch>
                <Match when={cfg.init}>
                  {props.children}
                  <Maintenance />
                </Match>

                <Match when={!!state.banned}>
                  <div class="flex h-[80vh] flex-col items-center justify-center gap-2"></div>
                </Match>

                <Match when={cfg.initLoading}>
                  <div class="flex h-[80vh] flex-col items-center justify-center gap-2">
                    <div>
                      {i18n.t.app_login_issues}{' '}
                      <a class="link" onClick={() => userStore.logout()}>
                        {i18n.t.app_logging_out}
                      </a>{' '}
                      {i18n.t.app_log_back_in}
                    </div>

                    <Show when={api.getFallbackApiUrl()}>
                      <div>
                        {i18n.t.app_page_loading_issues}{' '}
                        <a
                          class="link"
                          href={`https://xxxx.chat?api_url=${api.getFallbackApiUrl()}`}
                        >
                          {i18n.t.app_clicking_here}
                        </a>
                      </div>
                    </Show>

                    <Loading />
                  </div>
                </Match>

                <Match when>
                  <div class="flex flex-col items-center gap-2">
                    <div>{i18n.t.app_failed_to_load}</div>
                    <div>
                      <Button onClick={reload}>{i18n.t.app_try_again}</Button>
                    </div>
                  </div>
                </Match>
              </Switch>
            </div>
          </main>
        </div>
      </div>
      <Notifications />
      <ImpersonateModal
        show={page.showImpersonate}
        close={() => pageStore.toggleImpersonate(false)}
      />
      <InfoModal t={i18n.t} />
      <ProfileModal />
      <BannedModal t={i18n.t} />
      <GlobalFileInput />
      <ImageModal />
      <ImageSettingsModal />
      <SettingsModal />
      <BottomNav />
      <div
        class="absolute bottom-0 left-0 right-0 top-0 z-10 h-[100vh] w-full bg-black bg-opacity-20 sm:hidden"
        classList={{ hidden: !page.showMenu }}
        onClick={() => pageStore.closeMenu()}
      ></div>
      <Show when={!!page.confirm}>
        <Modal
          show={true}
          title={page.confirm?.title || i18n.t.confirm}
          close={() => pageStore.closeConfirm(false)}
          footer={
            <>
              <For each={page.confirm?.actions || []}>
                {(btn) => (
                  <Button
                    schema={btn.schema}
                    onClick={() => {
                      btn.onClick()
                      pageStore.closeConfirm(true)
                    }}
                  >
                    {btn.text}
                  </Button>
                )}
              </For>

              <Show
                when={page.confirm?.onConfirm}
                fallback={<Button onClick={() => pageStore.closeConfirm(false)}>{i18n.t.close}</Button>}
              >
                <Button onClick={() => pageStore.closeConfirm(false)}>{i18n.t.cancel}</Button>

                <Button schema="green" onClick={() => pageStore.closeConfirm(true)}>
                  {i18n.t.confirm}
                </Button>
              </Show>
            </>
          }
        >
          {page.confirm?.message}
        </Modal>
      </Show>
    </ContextProvider>
  )
}

const InfoModal: Component<{ t: Translations }> = (props) => {
  const state = pageStore((s) => ({ info: s.info }))

  return (
    <Modal
      title={state.info?.title || props.t.app_information}
      show={!!state.info?.content}
      close={() => pageStore.closeInfo()}
      maxWidth="half"
    >
      <Show when={typeof state.info === 'string'} fallback={state.info?.content}>
        <div class="markdown" innerHTML={markdown.makeHtml(state.info?.content)} />
      </Show>
    </Modal>
  )
}

const BannedModal: Component<{ t: Translations }> = (props) => {
  const state = userStore((u) => ({
    banned: u.banned,
  }))

  return (
    <Modal show={!!state.banned} close={() => userStore.logout()} title={props.t.app_account_banned}>
      <div class="flex flex-col items-center">
        <p class="">{props.t.app_banned_message}</p>
        <p class="my-2 flex items-center font-bold">{state.banned}</p>
        <p>{props.t.app_banned_contact}</p>
      </div>
    </Modal>
  )
}

export default App
